import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MobileLayout_S } from '~/core/configuration/configuration-global-config.schema';
import { useSERPState } from '~/core/state/SERPStateContext';
import { useSelectedTab } from '~/hooks/useSelectedTab';
import { TabResult, useTabsResults } from '~/hooks/useTabResults';
import SRError from '~/utils/errors/srError';
import styles from './tabs.module.scss';

const TabNavigation: React.FC = () => {
    const { config } = useSERPState();
    const { results } = useTabsResults();
    const { activeTab, setActiveTab: changeTab } = useSelectedTab();

    // Stav pro aktuálně focusnutý tab
    const [focusedTabIndex, setFocusedTabIndex] = useState<number>(0);

    // Reference na všechny tab tlačítka
    const tabsRef = useRef<Record<string, HTMLButtonElement | null>>({});

    const [tabs, tabsOrder] = useMemo<[Record<string, TabResult>, Array<string>]>(() => {
        const layoutPositionOrder = ['serp-products', 'serp-categories', 'serp-fulltext'];
        return results
            .sort((a, b) => {
                const aI = layoutPositionOrder.findIndex((v) => a.setState.layoutPosition.includes(v));
                const bI = layoutPositionOrder.findIndex((v) => b.setState.layoutPosition.includes(v));

                if (aI !== bI) {
                    return aI - bI;
                }

                return a.title.localeCompare(b.title);
            })
            .reduce(
                ([accTabs, accTabsOrder], tab) => [
                    { ...accTabs, [tab.setState.querySet.identifier]: tab },
                    [...accTabsOrder, tab.setState.querySet.identifier],
                ],
                [{} as Record<string, TabResult>, [] as string[]]
            );
    }, [results]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, id: string) => {
            const index = tabsOrder.findIndex((tabId) => tabId === id);
            if (index === -1) {
                throw new SRError(`Could not find index for tab with id ${id}`);
            }
            switch (e.key) {
                case 'ArrowRight':
                case 'ArrowLeft': {
                    const increment = e.key === 'ArrowLeft' ? -1 : 1;
                    const newFocusIndex = (index + tabsOrder.length + increment) % tabsOrder.length;
                    const newSelectedId = tabsOrder[newFocusIndex];
                    e.preventDefault();
                    setFocusedTabIndex(newFocusIndex);
                    tabsRef.current[newSelectedId]?.focus();
                    break;
                }
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    // Aktivace tabu na základě focusovaného indexu
                    changeTab(id);
                    break;
            }
        },
        [tabsOrder, changeTab]
    );

    const handleClick = useCallback(
        (id: string) => {
            const index = tabsOrder.findIndex((tabId) => tabId === id);
            if (index === -1) {
                throw new SRError(`Could not find index for tab with id ${id}`);
            }
            setFocusedTabIndex(index);
            changeTab(id);
        },
        [tabsOrder, setFocusedTabIndex, changeTab]
    );

    if (results.length <= 1 || config.mobileLayout !== MobileLayout_S.Values.tabs) {
        return null;
    }

    return (
        <>
            <div className={styles.tabsWrapper} role="tablist">
                {tabsOrder.map((tabId, index) => {
                    const selected = tabId === activeTab?.setState.querySet.identifier;
                    const tab = tabs[tabId];

                    return (
                        <button
                            key={tab.result.querySetIdentifier}
                            id={`tab-${tab.title}`}
                            role="tab"
                            aria-selected={selected ? 'true' : 'false'}
                            aria-controls={`tabpanel-${tab.title}`}
                            tabIndex={index === focusedTabIndex ? 0 : -1}
                            className={`${styles.tabButton} ${selected ? styles.active : ''} ${
                                tab.result.hits.length === 0 ? styles.empty : ''
                            }`}
                            onClick={() => handleClick(tabId)}
                            onKeyDown={(e) => handleKeyDown(e, tabId)}
                            ref={(el) => (tabsRef.current[tabId] = el)}
                        >
                            {tab.title}
                        </button>
                    );
                })}
            </div>
        </>
    );
};

export default TabNavigation;
